const FALLBACK_ALLERGEN_LABELS: Record<string, { en: string; es: string; ca?: string }> = {
  gluten: { en: "Gluten", es: "Gluten", ca: "Gluten" },
  crustaceans: { en: "Crustaceans", es: "Crustáceos", ca: "Crustacis" },
  eggs: { en: "Eggs", es: "Huevos", ca: "Ous" },
  fish: { en: "Fish", es: "Pescado", ca: "Peix" },
  peanuts: { en: "Peanuts", es: "Cacahuetes", ca: "Cacauets" },
  soy: { en: "Soy", es: "Soja", ca: "Soja" },
  dairy: { en: "Dairy", es: "Lácteos", ca: "Làctics" },
  tree_nuts: { en: "Tree Nuts", es: "Frutos de cáscara", ca: "Fruits de closca" },
  celery: { en: "Celery", es: "Apio", ca: "Api" },
  mustard: { en: "Mustard", es: "Mostaza", ca: "Mostassa" },
  sesame: { en: "Sesame", es: "Sésamo", ca: "Sèsam" },
  sulfites: { en: "Sulfites", es: "Sulfitos", ca: "Sulfits" },
  lupin: { en: "Lupin", es: "Altramuces", ca: "Tramussos" },
  mollusks: { en: "Mollusks", es: "Moluscos", ca: "Mol·luscs" },
};

export type ProductAllergen = {
  code: string;
  label: string;
};

const VOLQAN_ALLERGEN_ICON_BASE_URL = "https://cdn.volqan.com/files/shared/allergens";

const toText = (value: unknown) => String(value ?? "").trim();

function normalizeCode(value: unknown): string {
  return toText(value).toLowerCase();
}

function resolveFallbackLabel(code: string, language?: string): string {
  const bucket = FALLBACK_ALLERGEN_LABELS[normalizeCode(code)];
  const lang = normalizeCode(language).slice(0, 2);
  if (!bucket) return normalizeCode(code).replace(/_/g, " ");
  return bucket[lang as "en" | "es" | "ca"] || bucket.es || bucket.en;
}

function pickTranslatedLabel(source: Record<string, unknown>, language?: string): string {
  const lang = normalizeCode(language).slice(0, 2);
  const translations =
    source.translations && typeof source.translations === "object"
      ? (source.translations as Record<string, unknown>)
      : null;
  const nameValue =
    source.name && typeof source.name === "object"
      ? (source.name as Record<string, unknown>)
      : null;

  const direct =
    toText(source.label) ||
    toText(source.localizedName) ||
    toText(source.displayName) ||
    toText(source.title);
  if (direct) return direct;

  if (lang && nameValue && toText(nameValue[lang])) return toText(nameValue[lang]);
  if (lang && translations && translations[lang] && typeof translations[lang] === "object") {
    const bucket = translations[lang] as Record<string, unknown>;
    const translated = toText(bucket.label) || toText(bucket.name) || toText(bucket.title);
    if (translated) return translated;
  }

  return (
    toText(source.name) ||
    (nameValue ? toText(nameValue.es) || toText(nameValue.en) || toText(Object.values(nameValue)[0]) : "") ||
    ""
  );
}

export function normalizeProductAllergens(raw: unknown, language?: string): ProductAllergen[] {
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const normalized: ProductAllergen[] = [];

  for (const entry of raw) {
    if (typeof entry === "string") {
      const code = normalizeCode(entry);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      normalized.push({ code, label: resolveFallbackLabel(code, language) });
      continue;
    }

    if (entry && typeof entry === "object") {
      const obj = entry as Record<string, unknown>;
      const code = normalizeCode(obj.code ?? obj.id ?? obj.allergenCode ?? obj.slug ?? obj.key ?? obj.name);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      normalized.push({
        code,
        label: pickTranslatedLabel(obj, language) || resolveFallbackLabel(code, language),
      });
    }
  }

  return normalized;
}

export function buildAllergenIconUrl(code: string): string {
  const normalized = normalizeCode(code);
  return normalized ? `${VOLQAN_ALLERGEN_ICON_BASE_URL}/${encodeURIComponent(normalized)}.svg` : "";
}
